import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { ReactSVG } from 'react-svg';
import { transformPxValueToRem } from '../../../../businessCommon/src/@utils/fontSize';
import useInViewport from 'ahooks/lib/useInViewport';
import { iconMap } from './icons';
interface IReactIconProps {
    type: string; // 图标名称 更好的兼容Iconfont 组件
    iconSrc?: string; // 图标路径
    className?: string; // 类名
    color?: string; // 颜色
    style?: React.CSSProperties; // 样式
    size?: number; // 大小
    colorProp?: 'fill' | 'stroke'; // 颜色属性
    fillElement?: string; // 填充元素 //' path, rect, circle, ellipse, line, polyline, polygon'
    wrapper?: 'span' | 'div' | 'svg'; // 包裹元素 默认span
    hoverColor?: string; // 悬停颜色
    onClick?: (e: { stopPropagation: () => void; preventDefault: () => void }) => void; // 点击事件
    beforeInjection?: (svg: SVGElement) => void; // 注入前回调
    afterInjection?: (svg: SVGElement) => void; // 注入后回调
    onHover?: (isHover: boolean, ref?: any) => void; // 悬停事件
    onError?: (error: Error) => void; // 错误回调
}

interface IconInfo {
    svgSrc: string;
    iconName: string;
}

const defaultIconInfo: IconInfo = {
    svgSrc: '',
    iconName: '',
};

// const ICON_PATH = './icons';

// const convertToKebabCase = (str: string): string => str.replace(/([A-Z])/g, '-$1').toLowerCase();

/**
 * @description 图标组件
 * @param props
 * @returns
 */
const ReactIcon = memo((props: IReactIconProps) => {
    const {
        type: iconName,
        className,
        iconSrc = '',
        style = {},
        color = 'currentColor',
        hoverColor,
        size,
        //fillElement = 'path',
        colorProp = 'fill',
        wrapper = 'span',
        beforeInjection,
        onError,
    } = props;
    const svgRef = useRef<any>(null);
    const [IconInfo, setIconInfo] = useState(defaultIconInfo);
    const skeletonRef = useRef(null);
    const [inViewport] = useInViewport(skeletonRef);
    const prevIconSrcRef = useRef(iconSrc);

    const sizeRem = useMemo(() => (size ? `${transformPxValueToRem(size)}rem` : 'auto'), [size]);

    const onInitIcon = useCallback((): void => {
        if (!inViewport || IconInfo?.iconName === iconName) {
            return;
        }

        // 如果 iconSrc 发生变化，重置状态
        if (prevIconSrcRef.current !== iconSrc) {
            setIconInfo(defaultIconInfo);
            prevIconSrcRef.current = iconSrc;
        }

        if (iconSrc) {
            setIconInfo({
                svgSrc: iconSrc,
                iconName,
            });
            return;
        }
        setIconInfo({
            svgSrc: iconMap?.[iconName] || iconMap.welcome,
            iconName,
        });
        /* const fileName = convertToKebabCase(iconName);
        try {
            const icon = require(`${ICON_PATH}/${fileName}.svg`);
            if (!icon?.default) {
                throw new Error(`Invalid SVG file for icon: ${iconName}`);
            }
        } catch (error) {
            setIconInfo(defaultIconInfo);
            onError?.(error as Error);
        } */
    }, [iconName, inViewport, IconInfo?.iconName, iconSrc, onError]);

    const onHoverChange = useCallback(
        (isHover: boolean) => {
            if (svgRef.current?.reactWrapper && hoverColor) {
                const currentColor = isHover && hoverColor ? hoverColor : color;
                svgRef.current.reactWrapper.style[colorProp] = currentColor || '';
            }
            props?.onHover?.(isHover, svgRef.current);
        },
        [hoverColor, color, props?.onHover],
    );

    const styleSkeleton = useMemo(
        () => ({
            width: sizeRem === 'auto' ? '' : sizeRem,
            height: sizeRem === 'auto' ? '' : sizeRem,
            backgroundColor: color || '#f5f5f5',
            opacity: 0.02,
            ...style,
        }),
        [sizeRem, color, style],
    );

    const iconStyle = useMemo(
        () => ({
            [colorProp]: color,
            cursor: hoverColor || props.onClick ? 'pointer' : 'default',
            ...style,
        }),
        [color, hoverColor, props.onClick, style],
    );

    const handleBeforeInjection = useCallback(
        (svg: SVGElement) => {
            // 设置 SVG 尺寸
            svg.setAttribute('width', sizeRem);
            svg.setAttribute('height', sizeRem);
            /* if (!color) {
                // 颜色不存在时跟随父元素
                const elements = svg.querySelectorAll(fillElement);
                elements.forEach((element) => {
                    element.setAttribute(colorProp, color || 'currentColor');
                });
            } */
            // 调用自定义注入前处理函数
            beforeInjection?.(svg);
        },
        [sizeRem, color, beforeInjection],
    );

    useEffect(() => {
        onInitIcon();
    }, [onInitIcon]);

    return IconInfo?.svgSrc ? (
        <ReactSVG
            ref={svgRef}
            src={IconInfo?.svgSrc}
            wrapper={wrapper}
            className={`${className} inline-block`}
            style={iconStyle}
            afterInjection={props?.afterInjection}
            onClick={props?.onClick}
            onError={() => {
                setIconInfo(defaultIconInfo);
                onError?.(new Error(`Failed to load icon: ${iconName}`));
            }}
            beforeInjection={handleBeforeInjection}
            onMouseEnter={() => onHoverChange(true)}
            onMouseLeave={() => onHoverChange(false)}
        />
    ) : (
        <div className={`${className} border-radius-0.08rem`} style={styleSkeleton} ref={skeletonRef} />
    );
});

ReactIcon.displayName = 'ReactIcon';

export { IReactIconProps, ReactIcon };
